import axios from 'axios'
import aqe from '@pushly/aqe'
import type { PQueryBuilderField, PQueryFieldOption } from '@pushly/models/lib/interfaces/pquery-builder-field'

export type ItemGroupFieldValueLoader = (
    field: PQueryBuilderField,
    search: string,
    page: { offset: number; limit: number; signal: AbortSignal },
) => Promise<PQueryFieldOption[]>

/**
 * Reads the values an item-group field offers, a page and a search at a time.
 *
 * Handed to the item-group builder as `loadFieldValues`. The builder only calls it for a field the fields
 * endpoint marked `optionsTruncated` — one with more values than it could carry inline — and owns the
 * debouncing, paging and abandonment; this only makes the request.
 *
 * Build one per domain and keep it (for example with `useMemo`): the builder treats a new loader as a
 * reason to start every list over.
 */
export function createItemGroupFieldValueLoader(domainId: number): ItemGroupFieldValueLoader {
    return async (field, search, { offset, limit, signal }) => {
        // The transport predates abort signals, so the signal is adapted onto the cancellation it does
        // understand. A signal already raised must not start a request that nothing will stop.
        const cancellation = axios.CancelToken.source()
        const stop = () => cancellation.cancel()

        if (signal.aborted) stop()
        else signal.addEventListener('abort', stop, { once: true })

        try {
            const term = search.trim()
            const { data } = await axios.get(
                `${aqe.defaults.publicApiDomain}/domains/${domainId}/item-groups/fields/${encodeURIComponent(field.key)}/values`,
                {
                    cancelToken: cancellation.token,
                    // Omitted rather than sent empty where they carry no instruction, so two identical
                    // requests look identical in a log.
                    params: {
                        limit,
                        ...(offset > 0 ? { offset } : {}),
                        ...(term ? { search: term } : {}),
                    },
                },
            )

            const values: string[] = Array.isArray(data?.data)
                ? data.data.filter((value: unknown): value is string => typeof value === 'string' && value.length > 0)
                : []

            // A value is its own label: these are the catalog's own values, with nothing to resolve them to.
            return values.map((value) => ({ label: value, value }))
        } finally {
            signal.removeEventListener('abort', stop)
        }
    }
}
